import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import ShortUniqueId from 'short-unique-id';
import { updatecfObjectOptions1 } from '../../../redux/ChartPoperties/ChartControlsActions';
import './tablechartCF.css';
import { InputBase } from '@mui/material';
import { Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { CustomFontAndBgColor, StyleButtons } from '../CommonComponents';
import { RootState } from '../../../redux';
import { isColorDark } from '../../ChartOptions/Color/ChartColorComponents/colorUtils';
import { useEffect, useMemo } from 'react';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { getMinMaxForFormattedData } from '../../Charts/CrossTab/ConditionalColorFormat';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const closeIconStyle = {
  fontSize: '12px',
  cursor: 'pointer',
  float: 'right',
};

const inputBaseStyle = {
  border: '2px solid rgba(224,224,224,1)',
  borderRadius: '3px',
  height: '20px',
  fontSize: '12px',
  padding: '0px 4px',
  color: '#a7a7a7',
};
const checkbosStyle = {
  '&.Mui-checked': {
    color: '#2bb9bb',
  },
};

const GradientComponent = ({ format, gradientMinMax, iconColor }: any) => {
  const dispatch = useDispatch();
  const { chartControls, tabTileProps, tabState } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    tabState: state.tabState,
  }));

  const updateCF = (propKey: string, updated: any) =>
    dispatch(updatecfObjectOptions1(propKey, updated));

  format = JSON.parse(JSON.stringify(format));
  var propKey = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  var uId = new ShortUniqueId({ length: 8 });

  const [gradientValue, setGradientValue] = useState<any>(null);
  const [activeTheme, setActiveTheme] = useState<any>(null);

  const chartControl: any = chartControls.properties[propKey];
  const defaultThemeId = tabState.tabs[tabTileProps.selectedTabId]?.dashboardState?.defaultThemeId;

  const combinedThemes = useCombinedColorSchemes(); // or use your hook if needed
  const defaultThemeName = useMemo(() => {
    let theme = defaultThemeId
      ? combinedThemes.find((t) => t.id === defaultThemeId || t.themeId === defaultThemeId)
      : undefined;
    if (!theme && combinedThemes.length > 0) {
      theme = combinedThemes[0];
    }
    return theme?.name;
  }, [defaultThemeId, combinedThemes]);

  const themeName = chartControl.colorScheme || defaultThemeName || 'peacock';

  // 🔹 find matching theme
  useEffect(() => {
    const foundTheme =
      combinedThemes.find((t) => t.name === themeName) ||
      combinedThemes.find((t) => t.name === 'peacock');

    setActiveTheme(foundTheme || null);
  }, [themeName, combinedThemes]);

  // Commenting for now couldn't found its use case. This was causing hinderance in the UI
  // 🔹 Load default theme if set
  // useEffect(() => {
  //   const loadTheme = async () => {
  //     if (defaultThemeId) {
  //       const userThemes = await fetchUserThemes();
  //       const foundUserTheme = userThemes.find((t: any) => t.themeId === defaultThemeId);
  //       if (foundUserTheme) {
  //         setActiveTheme(foundUserTheme);
  //         return;
  //       }

  //       const foundSilzilaTheme = ColorSchemes.find((t) => t.id === defaultThemeId);
  //       if (foundSilzilaTheme) {
  //         setActiveTheme(foundSilzilaTheme);
  //         return;
  //       }
  //     }
  //     setActiveTheme(null);
  //   };

  //   (async () => {
  //     await loadTheme();
  //   })();
  // }, [defaultThemeId]);

  useEffect(() => {
    if (!activeTheme || !format?.value?.length) return;

    const items = format.value;

    const mapped = items.map((el: any) => {
      if (el.isColorChanged) return el;

      const name = el.name?.trim().toLowerCase();
      let themeIndex = 0;

      if (name === 'min') themeIndex = 0;
      else if (name === 'max') themeIndex = 1;
      else if (name === 'null' || name === '(blank)' || name === 'blank') themeIndex = 2;
      else if (name === 'mid value') themeIndex = 3;

      const col = activeTheme.colors[themeIndex];

      el.backgroundColor = col;
      el.fontColor = isColorDark(col) ? 'white' : 'black';

      return el;
    });

    onUpdateRule(mapped, format.name);
  }, [activeTheme, themeName, chartControl.colorScheme, defaultThemeName, format?.value?.length]);

  const isMidValueOutOfRange = () => {
    const minObj = format?.value?.find((v: any) => (v.name ?? '').trim() === 'Min');
    const midObj = format?.value?.find((v: any) => (v.name ?? '').trim() === 'Mid Value');
    const maxObj = format?.value?.find((v: any) => (v.name ?? '').trim() === 'Max');

    if (!minObj || !midObj || !maxObj) return false;

    const min = Number(minObj.value);
    const mid = Number(midObj.value);
    const max = Number(maxObj.value);

    if (isNaN(min) || isNaN(mid) || isNaN(max)) return false;

    return mid < min || mid > max;
  };

  const onGradientStyleChange = (
    optionName: string,
    value: any,
    id: string,
    columnName: string,
  ) => {
    /* finding  conditional format obj */
    const matchedObj = chartControls.properties[propKey].tableConditionalFormats.filter(
      (column: any) => {
        return column.name === columnName;
      },
    );

    /* updating values in the values(table data) of selected conditional Format obj */
    const matchedValue = matchedObj[0].value.map((el: any) => {
      if (el.id === id) {
        el[optionName] = value;
      }
      return el;
    });

    /* assigning updated values array to corresponding conditional format obj on tableConditionalFormats Array*/
    const updatedValues = chartControls.properties[propKey].tableConditionalFormats.map(
      (column: any) => {
        if (column.name === columnName) {
          column.value = matchedValue;
        }
        return column;
      },
    );

    /* sending updatedValues as payload to update state */
    updateCF(propKey, updatedValues);
  };

  const onChangeMinMaxValues = (e: any, index: number) => {
    const temp = format?.value?.map((el: any, i: number) => {
      if (i === index) {
        el.value = e.target.value;

        if (e.target.value) {
          el.isUserChanged = true;
        } else {
          el.isUserChanged = false;
        }
      }
      return el;
    });

    onUpdateRule(temp, format.name);
  };

  const getMinAndMaxValue = (column: string) => {
    const valuesArray =
      Array.isArray(chartControls.properties[propKey].chartData) &&
      chartControls.properties[propKey].chartData.map((el: any) => {
        return el[column];
      });
    if (valuesArray && valuesArray.length > 0) {
      const minValue = Number(Math.min(...valuesArray)).toFixed(2);
      const maxValue = Number(Math.max(...valuesArray)).toFixed(2);

      const minMaxValues = getMinMaxForFormattedData(minValue, maxValue);

      const finalMinValue = Number(minValue) < 1000 ? minValue : minMaxValues.formattedMinInNumber;
      const finalMaxValue = Number(maxValue) < 1000 ? maxValue : minMaxValues.formattedMaxInNumber;

      return { min: finalMinValue, max: finalMaxValue };
    } else {
      return { min: '', max: '' };
    }
  };

  const getMidValue = (column: string) => {
    const valuesArray = chartControls.properties[propKey].chartData.map((el: any) => {
      return el[column];
    });
    const minValue: any = Number(Math.min(...valuesArray)).toFixed(2);
    const maxValue: any = Number(Math.max(...valuesArray)).toFixed(2);

    return (Number(minValue) + Number(maxValue)) / 2;
  };

  const onUpdateRule = (updatedArray: any, columnName: string) => {
    const updatedValues = chartControls.properties[propKey].tableConditionalFormats.map(
      (column: any) => {
        if (column.name === columnName) {
          column.value = updatedArray;
        }
        return column;
      },
    );

    /* sending updatedValues as payload to update state*/
    updateCF(propKey, updatedValues);
  };

  const onAddMidValue = () => {
    var obj = {
      id: uId(),
      forNull: false,
      name: `Mid Value `,
      value: gradientValue,
      isBold: false,
      isItalic: false,
      isUnderlined: false,
      backgroundColor: '#E6FB0A',
      fontColor: 'black',
    };

    var formatItemValue = format.value;
    let indexvalue = 2;
    let min = format.value.find((val: any) => val.name == 'Min').value;
    let max = format.value.find((val: any) => val.name == 'Max').value;
    let midVal = (Number(min) + Number(max)) / 2;

    obj.value = midVal;

    formatItemValue.splice(indexvalue, 0, obj);
    setGradientValue(midVal);
    onUpdateRule(formatItemValue, format.name);
  };

  const onRemoveMidValue = () => {
    var formatItemValue = format.value;
    let indexvalue = 2;
    formatItemValue.splice(indexvalue, 1);

    onUpdateRule(formatItemValue, format.name);
  };
  return (
    <>
      {format?.value?.map((el: any, index: number) => {
        return (
          <div className="gradientComponentContainer">
            <div className="gradientCardContainer">
              {el.name?.trim() == 'Null' ? (
                <span style={{ color: iconColor }}>(Blank)</span>
              ) : (
                <>
                  <span
                    style={{ display: 'flex', flexGrow: 1, alignItems: 'center', color: iconColor }}
                  >
                    {el.name}
                    {el.name?.trim() === 'Mid Value' && isMidValueOutOfRange() && (
                      <WarningAmberIcon
                        sx={{
                          fontSize: '14px',
                          color: '#f59e0b',
                          ml: 0.5,
                        }}
                        titleAccess="Mid value is outside Min and Max range"
                      />
                    )}

                    {el.name?.trim() === 'Mid Value' ? (
                      <CloseIcon
                        onClick={() => onRemoveMidValue()}
                        sx={{ ...closeIconStyle, ml: 'auto', color: iconColor }}
                      />
                    ) : null}
                  </span>
                  <InputBase
                    value={el.value}
                    onChange={(e: any) => {
                      onChangeMinMaxValues(e, index);
                    }}
                    sx={inputBaseStyle}
                    placeholder={
                      el.isUserChanged
                        ? ''
                        : el.name == 'Min'
                          ? 'Enter Min Value (' + getMinAndMaxValue(format.name)?.min + ')'
                          : el.name == 'Max'
                            ? 'Enter Max Value (' + getMinAndMaxValue(format.name)?.max + ')'
                            : 'Enter Mid Value (' + getMidValue(format.name) + ')'
                    }
                  />
                </>
              )}
            </div>
            <div className="displayFlex">
              <StyleButtons
                isBold={el.isBold}
                isItalic={el.isItalic}
                isUnderlined={el.isUnderlined}
                onChangeStyleProps={(option: string, value: any) => {
                  onGradientStyleChange(option, value, el.id, format.name);
                }}
                iconColor={iconColor}
              />

              <CustomFontAndBgColor
                id={el.id}
                backgroundColor={el.backgroundColor}
                fontColor={el.fontColor}
                onChangeColorProps={(option: string, value: any) => {
                  onGradientStyleChange(option, value, el.id, format.name);

                  if (option === 'backgroundColor' || option === 'fontColor') {
                    onGradientStyleChange('isColorChanged', true, el.id, format.name);
                  }
                }}
              />
            </div>
          </div>
        );
      })}
      <div className="midvalue">
        <label style={{ color: iconColor }}>
          <Checkbox
            sx={checkbosStyle}
            className="checkboxStyle"
            size="small"
            checked={format.value.length === 4 ? true : false}
            onClick={() => {
              if (format.value.length === 4) {
                onRemoveMidValue();
              } else {
                onAddMidValue();
              }
            }}
          />
          Add Mid Value
        </label>
      </div>
    </>
  );
};

export default GradientComponent;
